import React, { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Cookies from "js-cookie";
import { useUser } from "../../Data/UserContext";
import { getAuthHeaders } from "../../utils/authHeaders";
import {
  clearPendingShopPurchase,
  normalizeShopCurrency,
  setPendingShopPurchase,
  trackPurchaseFailed,
} from "../../utils/frontendTrackingEvents";
import { trackRubyPurchased } from "../../utils/gtmEvents";

const SHOP_PAGE_PATH = "/shop";

function stripeErrorToPurchaseErrorType(error: { type?: string; code?: string } | null): string {
  if (!error) return "network_error";
  if (error.type === "card_error" || error.code === "card_declined") return "payment_declined";
  if (error.type === "validation_error") return "validation_error";
  return "network_error";
}

function amountMajorFromStripeOrder(amountMinor: number, currency: string): number {
  const c = String(currency || "inr").toLowerCase();
  if (c === "inr") return amountMinor / 100;
  return amountMinor;
}

function newAttemptId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const stripePromise = process.env.STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.STRIPE_PUBLISHABLE_KEY)
  : null;

interface StripePayBtnProps {
  closeModal: (status: boolean) => void;
  offerId: number;
  payAmount: number;
  updateRubies: (rubies: number) => void;
  offerType: string;
  onPaymentSuccess?: () => void;
  /** Called with the server's current price (major units) when it differs from `payAmount`. */
  onPriceChange?: (payAmount: number) => void;
}

interface OrderDetails {
  client_secret: string;
  receipt: string;
  amount: number;
  currency: string;
  email?: string;
  customer_name?: string;
  /** If present and terminal, do not render Elements (backend sent stale intent) */
  status?: string;
}

type OrderResult = { order: OrderDetails; error?: undefined } | { order?: undefined; error: string };

/** Payment went through but the wallet is not credited yet; the Stripe webhook finishes it. */
type PendingOutcome = "processing" | "unverified";

const PENDING_MESSAGES: Record<PendingOutcome, string> = {
  processing:
    "Your payment is processing. Your rubies will be added to your wallet as soon as your bank confirms it.",
  unverified:
    "Your payment went through, but we couldn't add your rubies yet. They should appear in your wallet within a few minutes. If they don't, please contact support.",
};

function StripePaymentForm({
  offerId,
  payAmount,
  createOrder,
  closeModal,
  updateRubies,
  onPaymentSuccess,
  onPriceChange,
}: {
  offerId: number;
  payAmount: number;
  createOrder: () => Promise<OrderResult>;
  closeModal: (status: boolean) => void;
  updateRubies: (rubies: number) => void;
  onPaymentSuccess?: () => void;
  onPriceChange?: (payAmount: number) => void;
}) {
  const { user } = useUser();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [elementReady, setElementReady] = useState(false);
  const [paymentElementKey, setPaymentElementKey] = useState(0);
  const [pendingOutcome, setPendingOutcome] = useState<PendingOutcome | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errorMessage) {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [errorMessage]);

  /** Credits the wallet for a succeeded PaymentIntent. Never closes the modal on failure: the user has paid. */
  async function verifyReceipt(
    receiptId: string,
    paymentIntentId: string,
    amount: number,
    currency: string
  ) {
    const session_token = Cookies.get("c8_session_token");
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await sleep(1500 * attempt);
      try {
        const orderResp = await fetch(
          `${process.env.API_URL}/offers/${offerId}/receipt/${receiptId}/verify?user_session=${session_token}`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ payment_intent_id: paymentIntentId }),
          }
        );
        const resp = await orderResp.json().catch(() => ({}));
        if (orderResp.status === 200) {
          clearPendingShopPurchase();
          trackRubyPurchased({
            user_id: session_token ?? "",
            purchase_amount: amount / 100,
            currency: currency,
          });
          // purchase_success: server Measurement Protocol (Stripe webhook / verify)
          updateRubies(resp.wallet.rubies);
          if (onPaymentSuccess) {
            onPaymentSuccess();
          } else {
            closeModal(true);
          }
          return;
        }
        if (resp?.status === "processing") {
          clearPendingShopPurchase();
          setPendingOutcome("processing");
          return;
        }
        if (orderResp.status === 403 || orderResp.status === 404) break;
      } catch (error) {
        console.error("Verify receipt error:", error);
      }
    }
    clearPendingShopPurchase();
    setPendingOutcome("unverified");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) {
      setErrorMessage("Payment form is still loading. Please wait and try again.");
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(null);
    const userId = user?.userId ?? Cookies.get("cr8_userId");
    const fallbackCurrency = "INR";
    const fallbackAmountMajor = Number(payAmount);
    const fallbackCurrencyNorm = normalizeShopCurrency(fallbackCurrency);
    let amountMajor = fallbackAmountMajor;
    let currencyNorm = fallbackCurrencyNorm;
    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        trackPurchaseFailed({
          user_id: userId,
          product_type: "ruby_pack",
          amount: fallbackAmountMajor,
          currency: fallbackCurrencyNorm,
          payment_method: "card",
          error_type: stripeErrorToPurchaseErrorType(submitResult.error),
          pathname: SHOP_PAGE_PATH,
        });
        setErrorMessage(submitResult.error.message ?? "Please complete your payment details.");
        return;
      }

      const orderResult = await createOrder();
      if (!orderResult.order) {
        trackPurchaseFailed({
          user_id: userId,
          product_type: "ruby_pack",
          amount: fallbackAmountMajor,
          currency: fallbackCurrencyNorm,
          payment_method: "card",
          error_type: "network_error",
          pathname: SHOP_PAGE_PATH,
        });
        setErrorMessage(`${orderResult.error} Please try again.`);
        return;
      }
      const orderDetails = orderResult.order;

      amountMajor = amountMajorFromStripeOrder(orderDetails.amount, orderDetails.currency);
      currencyNorm = normalizeShopCurrency(orderDetails.currency);

      // The offer list can be stale (e.g. a sale ended while the modal was open): never charge
      // a different amount than the one on screen without the user seeing it first.
      if (orderDetails.amount !== Math.round(Number(payAmount) * 100)) {
        elements.update({ amount: orderDetails.amount });
        onPriceChange?.(amountMajor);
        setErrorMessage(
          `The price of this pack has changed from ₹${payAmount} to ₹${amountMajor}. Press Pay to continue at the new price.`
        );
        return;
      }

      const returnUrl =
        `${window.location.origin}/shop-payment-return` +
        `?receipt=${encodeURIComponent(orderDetails.receipt)}&offerId=${offerId}`;

      setPendingShopPurchase({
        offerId,
        amountMinor: orderDetails.amount,
        currency: orderDetails.currency,
      });

      const result = await stripe.confirmPayment({
        elements,
        clientSecret: orderDetails.client_secret,
        redirect: "if_required",
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              name: orderDetails.customer_name ?? "Cricket 8 Customer",
              ...(orderDetails.email && { email: orderDetails.email }),
            },
          },
        },
      });

      const error = result.error;
      const paymentIntent = "paymentIntent" in result ? result.paymentIntent : null;

      if (error) {
        clearPendingShopPurchase();
        trackPurchaseFailed({
          user_id: userId,
          product_type: "ruby_pack",
          amount: amountMajor,
          currency: currencyNorm,
          payment_method: "card",
          error_type: stripeErrorToPurchaseErrorType(error),
          pathname: SHOP_PAGE_PATH,
        });
        // Stripe pins declines that carry no `param` to the card-number field and keeps
        // the element invalid until that field is edited, so remount it for a clean retry.
        if (error.type === "card_error" && !error.param) {
          setElementReady(false);
          setPaymentElementKey((k) => k + 1);
          setErrorMessage(
            `${error.message ?? "Your card was declined."} Please re-enter your card details or try a different card.`
          );
        } else {
          setErrorMessage(error.message ?? "Payment failed");
        }
        return;
      }

      if (paymentIntent?.status === "processing") {
        clearPendingShopPurchase();
        setPendingOutcome("processing");
        return;
      }

      const paymentIntentId =
        paymentIntent?.id ??
        (orderDetails.client_secret.includes("_secret_")
          ? orderDetails.client_secret.split("_secret_")[0]
          : null);

      if (paymentIntentId) {
        await verifyReceipt(
          orderDetails.receipt,
          paymentIntentId,
          orderDetails.amount,
          orderDetails.currency
        );
      } else {
        clearPendingShopPurchase();
        setPendingOutcome("unverified");
      }
    } catch (err) {
      console.error("Pay submit error:", err);
      clearPendingShopPurchase();
      trackPurchaseFailed({
        user_id: userId,
        product_type: "ruby_pack",
        amount: amountMajor,
        currency: currencyNorm,
        payment_method: "card",
        error_type: "network_error",
        pathname: SHOP_PAGE_PATH,
      });
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (pendingOutcome) {
    return (
      <div className="w-full px-2 space-y-4 flex flex-col items-center" role="status">
        <p className="bg-[#4E2681] px-4 py-3 text-center font-chakra text-[14px] leading-snug text-white">
          {PENDING_MESSAGES[pendingOutcome]}
        </p>
        <button
          type="button"
          onClick={() => closeModal(false)}
          style={{
            width: "240px",
            height: "45px",
            textAlign: "center",
            backgroundColor: "#D2FF00",
            color: "#1B062F",
            fontFamily: "anton",
          }}
        >
          Close
        </button>
      </div>
    );
  }

  const payDisabled = !stripe || !elements || !elementReady || isSubmitting;

  return (
    <form onSubmit={handleSubmit} className="w-full px-2 space-y-2">
      <PaymentElement
        key={paymentElementKey}
        options={{ layout: "tabs" }}
        onReady={() => setElementReady(true)}
        onChange={(event) => {
          if (!event.empty) setErrorMessage(null);
        }}
      />
      {errorMessage && (
        <div
          ref={errorRef}
          role="alert"
          className="flex flex-row items-start gap-2 bg-red px-3 py-2 font-chakra text-[14px] font-semibold leading-snug text-white"
        >
          <span
            aria-hidden
            className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-white font-anton text-[12px] text-red"
          >
            !
          </span>
          <span>{errorMessage}</span>
        </div>
      )}
      <div className="w-full flex justify-center pt-2">
        <button
          type="submit"
          disabled={payDisabled}
          aria-busy={isSubmitting}
          className="disabled:cursor-not-allowed disabled:opacity-50 transition-opacity"
          style={{
            width: "240px",
            height: "45px",
            textAlign: "center",
            backgroundColor: "#D2FF00",
            color: "#1B062F",
            fontFamily: "anton",
          }}
        >
          {isSubmitting ? "Processing…" : !elementReady ? "Loading…" : "Pay"}
        </button>
      </div>
    </form>
  );
}

export default function StripePayBtn({
  closeModal,
  offerId,
  payAmount,
  updateRubies,
  offerType,
  onPaymentSuccess,
  onPriceChange,
}: StripePayBtnProps) {
  async function getOrder(): Promise<OrderResult> {
    const session_token = Cookies.get("c8_session_token");
    try {
      const orderResp = await fetch(
        `${process.env.API_URL}/offers/${offerId}/order?user_session=${session_token}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ attempt_id: newAttemptId() }),
        }
      );
      if (orderResp.status !== 201) {
        const data = await orderResp.json().catch(() => ({}));
        return { error: data?.message ?? "Could not start the payment." };
      }
      const resp = await orderResp.json();
      return {
        order: {
          client_secret: resp.client_secret,
          receipt: resp.receipt,
          amount: resp.amount,
          currency: resp.currency,
          status: resp.status,
          ...(resp.email && { email: resp.email }),
          ...(resp.customer_name && { customer_name: resp.customer_name }),
        },
      };
    } catch (err) {
      console.error(err);
      return { error: "Could not reach the payment server." };
    }
  }

  if (!stripePromise) {
    return (
      <p className="bg-red px-3 py-2 font-chakra text-[14px] text-white m-2">
        Stripe is not configured (missing STRIPE_PUBLISHABLE_KEY).
      </p>
    );
  }

  const formAreaHeight = "min(60vh, 400px)";

  return (
    <div
      className="w-[100%] p-2 mb-[20px] flex flex-col items-center relative"
      style={{ minHeight: formAreaHeight }}
    >
      <Elements
        stripe={stripePromise}
        options={{
          mode: "payment",
          amount: Math.max(1, Math.round(Number(payAmount) * 100)),
          currency: "inr",
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#FFFFFF",
              colorText: "#FFFFFF",
              colorTextSecondary: "#FFFFFF",
              colorBackground: "#1B062F",
              colorDanger: "#df1b41",
            },
          },
        }}
      >
        <StripePaymentForm
          offerId={offerId}
          payAmount={payAmount}
          createOrder={getOrder}
          closeModal={closeModal}
          updateRubies={updateRubies}
          onPaymentSuccess={onPaymentSuccess}
          onPriceChange={onPriceChange}
        />
      </Elements>
    </div>
  );
}
